// BulkInvoiceModal.tsx (replace your existing component with this)
import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CustomDataGrid from "../../components/dms_common/CustomDataGrid";
import { bulkInvoiceModalColumnsMetaData } from "./ManageSecondaryOrderUtils";
import Header from "../../components/dms_common/Header";
import "./BulkInvoiceModal.css";
import RenderSvg from "../../components/renderSvg/RenderSvg";
import { handleBulkInvoicingAfterManualAllocation } from "./ManageStockAllocationUtils";
import { getBulkConfirmStatus } from "../../services/manageOrderService";
import { orderStatusConfig } from "../../types/types";
import CustomGenericModal from "../../components/dms_common/CustomGenericModal";
import { GenericModal, getGenericModalProps, initialGenericModalProps } from "../viewOrder/ViewOrderUtils";

interface BulkInvoiceModalProps {
  open: boolean;
  onClose: () => void;
  rows: any[]; // incoming rows (initial)
  totalOrders: number;
  totalOrderValue: number;
  orderBodyForPayload: any;
  bulkOrderTrackingStages: orderStatusConfig[];
  selectedOrders: any;
  setSelectedOrdersAfterInvoicing: React.Dispatch<React.SetStateAction<any>>;
}

const BulkInvoiceModal: React.FC<BulkInvoiceModalProps> = ({
  open,
  onClose,
  rows,
  totalOrders,
  totalOrderValue,
  orderBodyForPayload,
  bulkOrderTrackingStages,
  selectedOrders,
  setSelectedOrdersAfterInvoicing
}) => {
  const [checkedRows, setCheckedRows] = useState<string[]>([]);
  const [bulkId, setBulkId] = useState<string | undefined>(undefined);
  const [isBulkStillProcessing, setIsBulkStillProcessing] = useState<boolean>(false);
  const [genericModalProps, setGenericModalProps] = useState<GenericModal>(
    initialGenericModalProps
    );
  // internalRows holds the rows we show in the grid and get updated statuses
  const [internalRows, setInternalRows] = useState<any[]>([]);

    useEffect(() => {
        setInternalRows(Array.isArray(rows) ? rows : [])
    }, [rows])

  // sync internalRows when parent rows prop changes (fresh data)
  useEffect(() => {
    setInternalRows(Array.isArray(rows) ? rows : []);
    if (Array.isArray(rows)) {
      setCheckedRows(rows.map((r: any) => r.id ?? r.orderNumber));
    }
  }, [rows]);

  // reset when modal closes
  useEffect(() => {
    if (!open) {
      setBulkId(undefined);
      setIsBulkStillProcessing(false);
    }
  }, [open]);

  const handleBulkInvoicingClick = async () => {
    // Only keep checked rows that are not hidden/disabled
    const validCheckedRows = checkedRows.filter(
      (rowId) => !rowIdsWithCheckboxHidden.includes(rowId)
    );

    if (validCheckedRows.length === 0) {
      return;
    }

    const filteredPayload = {
      auto: (orderBodyForPayload?.auto || []).filter((orderNumber: any) =>
        validCheckedRows.includes(orderNumber)
      ),
      manual: (orderBodyForPayload?.manual || []).filter((order: any) =>
        validCheckedRows.includes(order.orderNumber)
      ),
    };

    try {
      setIsBulkStillProcessing(true)
      const res = await handleBulkInvoicingAfterManualAllocation(filteredPayload);

      if (res?.status === 208) {
        setBulkId(undefined);
        setIsBulkStillProcessing(true);
      } else if (res && res?.data?.bulkId) {
        const newBulkId = res.data.bulkId;
        setBulkId(newBulkId);
        setIsBulkStillProcessing(true);
      }
    } catch (error: any) {
      setGenericModalProps(
        getGenericModalProps("error", "Something went wrong")
      );
      setIsBulkStillProcessing(false)
    }
  };

  

  // Helper: parse raw API response (res.data) and return { statusMap, hasInProgress }
    // helper to convert API status -> UI bulkStatus
    const mapApiStatusToBulkStatus = (apiStatus?: string) => {
    if (!apiStatus) return "Loading";

    const s = String(apiStatus).trim().toUpperCase();

    // Failure variants -> Downloaded Fail
    if (["FAILURE", "FAIL", "FAILED", "DOWNLOAD_FAIL", "DOWNLOAD FAILURE"].includes(s)) {
        return "Downloaded Fail";
    }

    // Final positive/negative states that are not 'in progress'
    if (
        s === "CONFIRMED" ||
        s === "PARTIALLY CONFIRMED" ||
        s === "PARTIALLY INVOICED" ||
        s === "REJECTED" ||
        s === "INVOICED"
    ) {
        // return the api label if you want to show it, or return Loading to treat as not-loading.
        // Here we return the API label so you can display exact final state:
        return apiStatus;
    }

    // Anything else treat as still processing/loading
    return "Loading";
    };

// parse raw API response into map and whether any are still 'Loading'
    const parseBulkConfirmResponse = (apiData: any[]): { statusMap: Record<string, string>, hasInProgress: boolean } => {
        const statusMap: Record<string, string> = {};
        let hasInProgress = false;

        if (!Array.isArray(apiData)) return { statusMap, hasInProgress };

        apiData.forEach((entry) => {
            const orders = Array.isArray(entry?.orders) ? entry.orders : [];
            orders.forEach((o: any) => {
            const orderNumber = o?.orderNumber;
            const apiStatus = o?.status;
            if (!orderNumber) return;

            const bulkStatus = mapApiStatusToBulkStatus(apiStatus);
            statusMap[orderNumber] = bulkStatus;

            if (bulkStatus === "Loading") hasInProgress = true;
            });
        });

        return { statusMap, hasInProgress };
    };

    // apply the mapped statuses to rows (sets `bulkStatus`)
    const applyStatusMapToRows = (statusMap: Record<string, string>) => {
    if (!statusMap || Object.keys(statusMap).length === 0) return;

    setInternalRows((prevRows) =>
        prevRows.map((r) => {
        const key = r.orderNumber ?? r.id;
        if (!key) return r;
        const newStatus = statusMap[key];
        if (!newStatus) return r;
        return {
            ...r,
            bulkStatus: newStatus, // ui will use bulkStatus; renderBulkStatus(bulkStatus) will work
        };
        })
    );
    };

  const bulkProcessingRef = useRef<boolean>(false)

  useEffect(() => {
    bulkProcessingRef.current = isBulkStillProcessing
  }, [isBulkStillProcessing])

  // Polling effect — uses getBulkConfirmStatus directly to obtain raw response (your pasted JSON shape)
  useEffect(() => {

    let cancelled = false;

    const poll = async () => {
      while (!cancelled && bulkProcessingRef.current) {
        try {
          const res = await getBulkConfirmStatus(bulkId);
          const apiData = res?.data ?? [];
          const { statusMap, hasInProgress } = parseBulkConfirmResponse(apiData);

          // update UI rows
          applyStatusMapToRows(statusMap);

          setIsBulkStillProcessing(!!hasInProgress);
          bulkProcessingRef.current = (!!hasInProgress)

          if (!hasInProgress) {
            // done
            break;
          }
        } catch (err) {
          console.error("poll error:", err);
          // Continue polling on temporary errors — or break if you want to stop after X failures
        }

        // wait 1s before next poll
        // avoid overlapping calls by using await
        await new Promise((r) => setTimeout(r, 1000));
      }
    };

    poll();

    return () => {
      cancelled = true;
    };
  }, [bulkId, isBulkStillProcessing]);

  const handleSetCheckedIdArray = (ids: any[]) => {
    setCheckedRows(ids);
  };


  const [rowIdsWithCheckboxHidden, setRowIdsWithCheckboxHidden] = useState<string[]>([]);

    useEffect(() => {
      const hiddenIds = internalRows
        .filter(row => {
          const status = (row.bulkStatus ?? row.status ?? "").toUpperCase();
          return status === "INVOICED" || status === "PARTIALLY INVOICED" || status === "REJECTED";
        })
        .map(row => row.id ?? row.orderNumber);
    setSelectedOrdersAfterInvoicing((prev :any) =>
      prev.filter((id: any) => !hiddenIds.includes(id.orderNumber))
    );
    setRowIdsWithCheckboxHidden(hiddenIds);
    }, [internalRows]);

  const [anyHasSkuRepl, setAnyHasSkuRepl] = useState<boolean>(false)

  useEffect(() => {
    const hasReplacement = internalRows?.some((row) =>
      Array.isArray(row?.sku) &&
      row.sku.some((skuEle: any) => Array.isArray(skuEle?.replacements) && skuEle.replacements.length > 0)
    ) ?? false;

    setAnyHasSkuRepl(hasReplacement);
  }, [internalRows]);

  const handleClose = () => {
    if (bulkProcessingRef.current) {
      setGenericModalProps(getGenericModalProps("positive", "Bulk Invoicing in progress. Please wait"))
      return;
    }
    onClose()

  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          height: "600px",
          display: "flex",
          flexDirection: "column",
          border: "1px solid #D9D9D9",
          borderRadius: "8px",
          boxShadow: 3,
          maxWidth: "fit-content",
          width: "auto",
        },
      }}
    >   <CustomGenericModal
            type={genericModalProps?.type}
            message={genericModalProps?.message}
            leftButtonText={genericModalProps?.leftButtonText}
            modalOpen={genericModalProps?.open}
            onClose={() => setGenericModalProps(initialGenericModalProps)}
          />
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        px={2}
        py={1.5}
        flexShrink={0}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton onClick={handleClose} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" fontWeight={600}>
            Bulk Invoice Summary
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          py: 1.5,
          px: 3,
          overflow: "hidden",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box
            sx={{
              fontSize: "0.9rem",
              fontWeight: 500,
              "& .Header_label": { fontSize: "0.9rem" },
              "& .Header_label_bar": { fontSize: "0.9rem" },
            }}
          >
            <Header title="Outlet Wise Summary Data" />
          </Box>

          {anyHasSkuRepl && <Box display="flex" alignItems="center" gap={1} className="invoiceSummary-disclaimer">
            <RenderSvg
              name="disclaimerIcon"
              customStyles={{ color: "#B96D16", width: "12px", height: "12px" }}
            />
            <span>Some of the SKUs has been replaced with other SKUs</span>
          </Box>}
        </Stack>

        <DialogContent
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            p: 0,
            borderRadius: "6px",
            overflow: "hidden",
            my: 1.5,
          }}
        >
          <CustomDataGrid
            // IMPORTANT: pass internalRows so status updates reflect in the grid
            rows={Array.isArray(internalRows) ? internalRows : []}
            columnsMetaData={bulkInvoiceModalColumnsMetaData()}
            tableWidth={0.723}
            isCheckboxSelection={true}
            isAnyPendingOrder={checkedRows.some(rowId => {
              const row = internalRows.find(r => (r.id ?? r.orderNumber) === rowId);
              return (
                (row?.bulkStatus?.toUpperCase() === "PENDING" || row?.bulkStatus?.toUpperCase() === "DOWNLOADED FAIL") &&
                !rowIdsWithCheckboxHidden.includes(rowId)
              );
            })}
            setCheckedIdArray={handleSetCheckedIdArray}
            checkedIdArray={checkedRows}
            enableRowClickRedirect={false}
            insideBulkInvoiceModal={true}
            orderStatusConfig={bulkOrderTrackingStages}
            handleBulkInvoicingForManualStockAllocation={handleBulkInvoicingClick}
            rowIdsWithCheckboxHidden={rowIdsWithCheckboxHidden}
            footerButtonLoading={bulkProcessingRef.current}
          />
        </DialogContent>
      </DialogContent>
    </Dialog>
  );
};

export default BulkInvoiceModal;
